import { i18nNsFactory } from '@atlas-frontend/i18n';
import { z } from 'zod/v3';
import {
  errorMap,
  transformNull,
  zIsoDate,
  zValidPeriod,
} from '../../common/utils';
import { zIsoDateTime } from '../../common/utils/schema/zIsoDateTime';
import { zTimeHoursMinutes } from '../../common/utils/schema/zTimeHoursMinutes';
import { ChangedBySchema } from '../../user/schema/ChangedBySchema';
import translations from '../translations/validation.translations.json';
import {
  ExecutionCalendarFormDataType,
  ExecutionDeadlineFormDataType,
  ExecutionPeriodFormDataType,
  ExecutionPublicationFormDataType,
} from './types';

const { addTranslations, t } = i18nNsFactory('execution-form-schema');
addTranslations(translations);

export const ExecutionCalendarSchema = z.object({
  id: z.number().nullable(),
  executionId: z.number().nullable(),
  executionDate: zIsoDate(),
  validPeriod: zValidPeriod(),
  sourceDataVersionId: z.number().nullable(),
  createdAt: zIsoDateTime().nullish(),
  createdBy: ChangedBySchema.nullish(),
  modifiedAt: zIsoDateTime().nullish(),
  modifiedBy: ChangedBySchema.nullish(),
});

export const ExecutionPeriodEditSchema = z.object({
  id: z.number().nullable(),
  editId: z.string(),
  existingPeriod: z.boolean().nullable(),
  executionId: z.number().nullable(),
  months: z.array(z.string()).nullable(),
  weekdays: z.array(z.string()).nullable(),
  days: z.array(z.string()).nullable(),
  hours: z.array(z.string()).nullable(),
  minutes: z.array(z.string()).nullable(),
  bankingDays: z.boolean().nullable(),
  validPeriod: zValidPeriod(),
  periodCount: z.number().nullable(),
  sourceDataVersionId: z.number().nullable(),

  createdAt: zIsoDateTime().nullish(),
  createdBy: ChangedBySchema.nullish(),
  modifiedAt: zIsoDateTime().nullish(),
  modifiedBy: ChangedBySchema.nullish(),
});

export const ExecutionPeriodsEditFormSchema = z.object({
  periods: z.array(ExecutionPeriodEditSchema),
});

export const ExecutionPeriodSchema = z.object({
  id: z.number().nullable(),
  executionId: z.number().nullable(),
  monthsPredefined: z.string().nullable(),
  monthsScheduled: z.array(z.string()).nullable(),
  weekdaysPredefined: z.string().nullable(),
  weekdaysScheduled: z.array(z.string()).nullable(),
  daysPredefined: z.string().nullable(),
  daysScheduled: z.array(z.string()).nullable(),
  hoursPredefined: z.string().nullable(),
  hoursScheduled: z.array(z.string()).nullable(),
  minutesPredefined: z.string().nullable(),
  minutesScheduled: z.array(z.string()).nullable(),
  bankingDays: z.boolean().nullable(),
  validPeriod: zValidPeriod(),
  periodCount: z.number().nullable(),
  sourceDataVersionId: z.number().nullable(),

  createdAt: zIsoDateTime().nullish(),
  createdBy: ChangedBySchema.nullish(),
  modifiedAt: zIsoDateTime().nullish(),
  modifiedBy: ChangedBySchema.nullish(),
});

export const ExecutionPublicationSchema = z.object({
  id: z.number().nullable(),
  executionId: z.number().nullable(),
  executionDate: zIsoDate(),
  validPeriod: zValidPeriod(),
  createdAt: zIsoDateTime().nullish(),
  createdBy: ChangedBySchema.nullish(),
  modifiedAt: zIsoDateTime().nullish(),
  modifiedBy: ChangedBySchema.nullish(),
});

export const ExecutionDeadlineSchema = z.object({
  id: z.number().nullable(),
  executionId: z.number().nullable(),
  valueDate: zIsoDate(() => t('validation.valueDate.required')),
  dueDate: zIsoDate(() => t('validation.dueDate.required')),
  dueTime: zTimeHoursMinutes(() => t('validation.dueTime.required')),
  createdAt: zIsoDateTime().nullish(),
  createdBy: ChangedBySchema.nullish(),
  modifiedAt: zIsoDateTime().nullish(),
  modifiedBy: ChangedBySchema.nullish(),
});

const ExecutionBaseFormDataSchema = z.object({
  id: z.number().nullable(),
  createdAt: zIsoDateTime().nullish(),
  createdBy: ChangedBySchema.nullish(),
  modifiedAt: zIsoDateTime().nullish(),
  modifiedBy: ChangedBySchema.nullish(),
  dataObjectGroupId: z.number().nullable(),
  autoLoadingPaused: z.boolean().nullish(),
  loadSample: z.boolean().nullable(),
  sampleLoadingDays: z.number().nullable(),
  sampleLoadingTime: z.string().nullable(),
  executionMethodId: z.number().nullable(),
  executionTime: z.string().nullable(),
  periodLoadingTypeId: z.number().nullable(),
  periodCount: z.number().nullable(),
  periodSkipCount: z.number().nullable(),
  logLoadingSuccess: z.boolean().nullable(),
  publicationTime: z.string().nullable(),
  sourceDataVersionId: z.number().nullable(),
  executionCalendar: transformNull<ExecutionCalendarFormDataType[]>(
    z.array(ExecutionCalendarSchema),
    [],
  ),
  executionPeriods: transformNull<ExecutionPeriodFormDataType[]>(
    z.array(ExecutionPeriodSchema),
    [],
  ),
  executionPublications: transformNull<ExecutionPublicationFormDataType[]>(
    z.array(ExecutionPublicationSchema),
    [],
  ),
});

export const ExecutionFormDataSchema = ExecutionBaseFormDataSchema.extend({
  autoLoadingPaused: z.boolean().nullish(),
  loadSample: z.boolean(),
  periodCount: z.number().nullable(),
  periodSkipCount: z.number().nullable(),
  sampleLoadingDays: z.number().nullable(),
  executionDeadlines: transformNull<ExecutionDeadlineFormDataType[]>(
    z.array(ExecutionDeadlineSchema),
    [],
  ),
});

export const ExecutionValidFormDataSchema = ExecutionBaseFormDataSchema.extend({
  autoLoadingPaused: z.boolean().nullish(),
  loadSample: z.boolean(),
  periodCount: z
    .number(errorMap(() => t('validation.bigger_than_zero')))
    .min(1)
    .nullable(),
  periodSkipCount: z
    .number(errorMap(() => t('validation.negative')))
    .min(0)
    .nullable(),
  sampleLoadingDays: z
    .number(errorMap(() => t('validation.negative')))
    .min(0)
    .nullable(),
});
